/**
 * BF bundle — the campaign's line predicates, for theme JavaScript.
 *
 * The single JS home of "what counts as a sofa / cover / cushion set /
 * schutzset" over /cart.js items, and of the per-gift status derived from
 * them. MIRROR OF: snippets/kc-bf-line-flags.liquid (theme Liquid). The two
 * must agree or the surfaces drift apart. (The configurator's bfBundle and
 * the checkout-bf-bundle extension still carry the v1 rules and are not part
 * of this offer.)
 *
 * A plain window global, not a module: the theme's own campaign scripts
 * (kc-bf-offer.js, kc-bf-cart-gift.js) are classic scripts, and this must
 * load before them via plain <script defer> ordering. Consumers guard on
 * `window.KcBfRules` existing and — critically — NEVER remove cart lines when
 * it is missing: during a deploy window an old cached asset next to new
 * markup must degrade to doing nothing, not to deleting a customer's gift.
 *
 * Why titles as well as types: every 3D-configurator product carries
 * product_type 'configurator' — sofas, covers and the cushion sets alike — so
 * the type identifies nothing there. Configurator sofas are the parent line
 * titled "«Model» Configuration"; configurator covers are titled
 * "Stoffbezug - …". The Stoffbezug exclusion inside the sofa rule is
 * load-bearing: replacement covers are titled "Stoffbezug - Nina
 * Configuration" and would otherwise pass the Configuration suffix test.
 * `product_title` is used, never the line `title` (it carries the variant
 * suffix). `_part` properties ('parent' / 'cover') back the titles up with a
 * locale-immune signal.
 *
 * Third mirror, and the one that decides the money: the Shopify Function
 * livom-apps/…/extensions/bf-bundle-gift (Rust). It re-runs these predicates
 * over the checkout cart and zeroes ONLY `_bf_gift` lines, only with a paid
 * sofa + cover. This file decides when gifts are ADDED; that one decides
 * whether they cost 0 — so a stale copy of this file can at worst add a line
 * the customer sees charged until the next cart render removes it.
 */
(function () {
  "use strict";

  if (window.KcBfRules) return;

  /** Config parsed from a [data-kc-bf-rules] (cart surfaces) or
   *  [data-bf-config] (PDP) node. Every field has a fallback so a stale
   *  render degrades to the OLD behaviour, never to something new and wrong. */
  function readConfig(node) {
    if (!node) return null;

    var giftA = node.getAttribute("data-bf-gift-a") || "kissen-set-1";
    var giftB = node.getAttribute("data-bf-gift-b") || "kissen-set-2";

    var setHandlesRaw = node.getAttribute("data-bf-set-handles");

    function list(attribute, lower) {
      return (node.getAttribute(attribute) || "")
        .split(",")
        .map(function (value) {
          value = value.trim();
          return lower ? value.toLowerCase() : value;
        })
        .filter(Boolean);
    }

    return {
      giftA: giftA,
      giftB: giftB,
      schutzset: node.getAttribute("data-bf-schutzset") || "komplettes-schutzset",
      sofaTypes: list("data-bf-sofa-types", true),
      coverType: (node.getAttribute("data-bf-cover-type") || "Sofabezug").toLowerCase(),
      // Legacy markup carries no set list: fall back to the storefront pair,
      // which is exactly what the code compared against before the list existed.
      setHandles: setHandlesRaw
        ? setHandlesRaw.split(",").map(function (v) { return v.trim(); }).filter(Boolean)
        : [giftA, giftB],
      // Product IDs — the only identifier Shopify does NOT translate. Handles
      // are localized (`kissen-set-1` is `cushion-set-1` in English), which is
      // why an empty list here has to keep working: it falls through to the
      // handle comparisons below, i.e. the old German-only behaviour, rather
      // than matching nothing at all.
      setIds: list("data-bf-set-ids", false),
      schutzsetId: (node.getAttribute("data-bf-schutzset-id") || "").trim(),
      // Black Weeks 2026: the cover voucher's product ids (kc-bf-bundle
      // bf[19]). A line of it is a cover for the gifts, never a sofa piece.
      voucherIds: list("data-bf-voucher-ids", false),
      // ...and the codes that stand in for the sofa (kc-bf-bundle bf[21]):
      // the post-purchase cover codes, lowercase
      giftCodes: list("data-bf-gift-codes", true),
      // Black Weeks 2026, another set instead of the gifted one (Ivan 20/10):
      // EVERY cushion set counts as "the customer has their set" (the product
      // types of a set, kc-bf-bundle bf[16], translated, plus any handle or
      // title naming a Kissenset), and Set 1's worth in whole euros (bf[22])
      // for copy. The Function does the discounting (`setOneValue`).
      setTypes: list("data-bf-set-types", true).length ? list("data-bf-set-types", true) : ["kissen set", "pillow set"],
      // ...and the same for the Schutzset (Ivan 23/10): ANY cleaning product
      // (its product type, kc-bf-bundle bf[28]) counts as "the customer has
      // their care set"; the Function credits the Schutzset's worth on it
      careTypes: list("data-bf-care-types", true).length ? list("data-bf-care-types", true) : ["cleaning products"],
      setOneValue: parseInt(node.getAttribute("data-bf-set-one-value") || "0", 10) || 0,
      coverPrefixes: list("data-bf-cover-prefix", true).length
        ? list("data-bf-cover-prefix", true)
        : ["stoffbezug"],
      sofaSuffix: (node.getAttribute("data-bf-sofa-suffix") || "Configuration").toLowerCase(),
      // Umbral en centimos que el sofa/configuracion debe alcanzar para
      // ganar cualquier regalo. 0 (el default y lo que emite DE) = sin
      // umbral, comportamiento identico al anterior. CH emite 250000.
      minAmount: parseInt(node.getAttribute("data-bf-min-amount") || "0", 10) || 0,
      // The COMPLETE cover (27/10, after CH #13633): sofa product id -> its own whole-sofa cover id (bf[30]), and
      // for the configurator pieces in the cart, piece product id -> its cover product id (their
      // `custom.replacement_cover`, rendered by kc-bf-cart-gift.liquid: /cart.js carries no metafields).
      // null on a stale render without the attribute: the old "any cover line" rule.
      sofaCovers: node.hasAttribute("data-bf-sofa-covers") ? parsePairs(node.getAttribute("data-bf-sofa-covers")) : null,
      pieceCovers: parsePairs(node.getAttribute("data-bf-piece-covers")),
      // Black Weeks 2026, Nora (kc-bf-bundle bf[23] / bf[24]): the per-MODEL
      // cushion-set gift that replaces Kissen Set 1, and the models whose sofa
      // ALONE earns the gifts (no cover, no minimum). Both empty on a stale
      // render, which is exactly the old behaviour.
      modelGifts: parseModelGifts(node.getAttribute("data-bf-model-gifts")),
      soloModels: list("data-bf-solo-models", true),
    };
  }

  /** "nora:15686851690878:kissen-set-8,…" -> [{model, productId, handle}].
   *  An entry without a model word or a handle is dropped: the handle is what
   *  the gift line carries in `_bf_gift`, so without it nothing could tell
   *  the model's gift apart from Set 1. */
  function parseModelGifts(raw) {
    return String(raw || "")
      .split(",")
      .map(function (entry) {
        var parts = entry.split(":");
        return {
          model: String(parts[0] || "").trim().toLowerCase(),
          productId: String(parts[1] || "").trim(),
          handle: String(parts[2] || "").trim(),
        };
      })
      .filter(function (entry) {
        return entry.model && entry.handle;
      });
  }

  /** "a:b[:x],c:d" -> {a: "b", c: "d"} */
  function parsePairs(raw) {
    var out = {};
    String(raw || "")
      .split(",")
      .forEach(function (entry) {
        var parts = entry.split(":");
        if (parts[0] && parts[1]) out[parts[0].trim()] = parts[1].trim();
      });
    return out;
  }

  /**
   * Is the WHOLE sofa covered (27/10, after CH #13633: a Kissenset's cover earned the set)? A storefront sofa's
   * OWN cover (cfg.sofaCovers), every body piece of a configuration with its cover in that configuration (as many
   * as the pieces), or the voucher. A cushion's, a Kissenset's, a single piece's or another sofa's cover is not.
   * MIRROR OF: complete_cover_lines in the bf-bundle-gift Function, bf_complete_cover in kc-bf-cart-gift.liquid.
   */
  function hasCompleteCover(items, cfg) {
    if (!cfg.sofaCovers) return items.some(function (i) { return isCoverLine(i, cfg); });
    if (items.some(function (i) { return isVoucherLine(i, cfg); })) return true;
    var ids = items.map(productId);
    var storefront = items.some(function (i) {
      if (part(i)) return false;
      var cover = cfg.sofaCovers[productId(i)];
      return !!cover && ids.indexOf(cover) !== -1;
    });
    if (storefront) return true;
    var sets = {};
    items.forEach(function (i) {
      var set = i.properties && i.properties._set_id;
      if (set) (sets[set] = sets[set] || []).push(i);
    });
    return Object.keys(sets).some(function (set) {
      var need = {};
      var have = {};
      var pieces = 0;
      sets[set].forEach(function (i) {
        var qty = Number(i.quantity) || 1;
        if (part(i) === "body") {
          var cover = (cfg.pieceCovers || {})[productId(i)];
          if (!cover) return;
          need[cover] = (need[cover] || 0) + qty;
          pieces++;
        } else if (part(i) === "cover") {
          have[productId(i)] = (have[productId(i)] || 0) + qty;
        }
      });
      return pieces > 0 && Object.keys(need).every(function (cover) { return (have[cover] || 0) >= need[cover]; });
    });
  }

  /** A whole-sofa cover of ANY sofa, or the voucher: what a gift code (the sofa bought before) needs. */
  function hasAnyWholeCover(items, cfg) {
    if (!cfg.sofaCovers) return items.some(function (i) { return isCoverLine(i, cfg); });
    var covers = Object.keys(cfg.sofaCovers).map(function (k) { return cfg.sofaCovers[k]; });
    return items.some(function (i) { return isVoucherLine(i, cfg) || covers.indexOf(productId(i)) !== -1; });
  }

  /** /cart.js gives product_id as a number; the config carries strings. */
  function productId(item) {
    return item.product_id === undefined || item.product_id === null
      ? ""
      : String(item.product_id);
  }

  /** Does the product title start with any of the cover prefixes? */
  function hasCoverPrefix(item, cfg) {
    var title = productTitle(item);
    return cfg.coverPrefixes.some(function (prefix) {
      return title.indexOf(prefix) === 0;
    });
  }

  function productTitle(item) {
    // product_title, never title: the line title carries the variant suffix
    // and breaks the endsWith below.
    return String(item.product_title || "").toLowerCase();
  }

  function part(item) {
    return String((item.properties && item.properties._part) || "");
  }

  // ID first, handle second. Handles are translated per locale, so the handle
  // comparison alone answered "no" to every English cart — which read as "the
  // customer has no set yet" and re-offered a gift they already had.
  function isSetLine(item, cfg) {
    if (cfg.setIds.indexOf(productId(item)) !== -1) return true;
    // A model's own gift set (Kissen Set 8 for Nora) is a campaign set too.
    // MIRROR OF: `is_set` in snippets/kc-bf-line-flags.liquid.
    if (modelGiftByProduct(item, cfg)) return true;
    return cfg.setHandles.indexOf(item.handle) !== -1;
  }

  /**
   * Black Weeks 2026, per-model gifts (Nora, Oct 2026). The model is the
   * first word of the list found in the sofa line's product title or handle,
   * lowercase — the same match as the cover % table (kc-bf-cover-off.liquid,
   * bf[20]). Titles and handles are translated, model names are not.
   */
  function modelHaystack(item) {
    return (productTitle(item) + " " + String(item.handle || "")).toLowerCase();
  }

  /** The solo model ("nora") of a sofa line, or "" for any other sofa. */
  function soloModelOf(item, cfg) {
    var models = cfg.soloModels || [];
    if (!models.length) return "";
    var haystack = modelHaystack(item);
    for (var i = 0; i < models.length; i++) {
      if (haystack.indexOf(models[i]) !== -1) return models[i];
    }
    return "";
  }

  /** The {model, productId, handle} gift of a sofa line's model, or null. */
  function modelGiftOf(item, cfg) {
    var gifts = cfg.modelGifts || [];
    if (!item || !gifts.length) return null;
    var haystack = modelHaystack(item);
    for (var i = 0; i < gifts.length; i++) {
      if (haystack.indexOf(gifts[i].model) !== -1) return gifts[i];
    }
    return null;
  }

  /** Is this line the PRODUCT of one of the model gifts (gifted or bought)? */
  function modelGiftByProduct(item, cfg) {
    var gifts = cfg.modelGifts || [];
    var id = productId(item);
    if (!id) return null;
    for (var i = 0; i < gifts.length; i++) {
      if (gifts[i].productId && gifts[i].productId === id) return gifts[i];
    }
    return null;
  }

  /** Is `_bf_gift` on this line one of the model gifts' handles? */
  function modelGiftByTag(item, cfg) {
    var tag = item && item.properties && item.properties._bf_gift;
    if (!tag) return null;
    var gifts = cfg.modelGifts || [];
    for (var i = 0; i < gifts.length; i++) {
      if (gifts[i].handle === tag) return gifts[i];
    }
    return null;
  }

  /** The Schutzset, or any other cleaning product (Black Weeks 2026, 23/10). */
  function isCareLine(item, cfg) {
    if (isSchutzsetLine(item, cfg)) return true;
    var type = String(item.product_type || "").toLowerCase();
    return (cfg.careTypes || []).indexOf(type) !== -1;
  }

  function isSchutzsetLine(item, cfg) {
    if (cfg.schutzsetId && productId(item) === cfg.schutzsetId) return true;
    return item.handle === cfg.schutzset;
  }

  /**
   * Is this line ANY cushion set: the gift, a bought Kissen Set 5, a
   * configurator harvey-kissenset-3? Black Weeks 2026 (Ivan 20/10): a
   * customer who keeps another set instead of the gifted Kissen Set 1 is not
   * handed Set 1 on top of it; the Function moves Set 1's worth onto the set
   * they kept, so adding Set 1 here would only take that away. MIRROR OF:
   * `bundle` in snippets/kc-bf-line-flags.liquid and is_any_set_line in the
   * Function.
   */
  function isAnySetLine(item, cfg) {
    if (isSetLine(item, cfg)) return true;
    var type = String(item.product_type || "").toLowerCase();
    if (cfg.setTypes && cfg.setTypes.indexOf(type) !== -1) return true;
    if (String(item.handle || "").toLowerCase().indexOf("kissenset") !== -1) return true;
    return productTitle(item).indexOf("kissenset") !== -1;
  }

  /**
   * Is this line the SECOND cushion set specifically?
   *
   * Los dos sets no se ganan igual: el Set 1 va con el sofa, pero el Set 2
   * solo se desbloquea si hay cover. Tratarlos como "un set" cualquiera
   * dejaba al cliente con un Set 2 marcado como gratis despues de quitar el
   * cover — un regalo que ya no le corresponde y que el BXGY dejaria de
   * poner a 0 en el checkout.
   *
   * bf[10] lista los ids en orden: [set1, set2, harvey1, harvey2], asi que
   * el segundo es el Set 2. Fallback por handle para markup viejo.
   */
  function isSetBLine(item, cfg) {
    if (cfg.setIds.length > 1 && productId(item) === cfg.setIds[1]) return true;
    return !!cfg.giftB && item.handle === cfg.giftB;
  }

  /**
   * The cover voucher (Black Weeks 2026, "Sofabezug-Geschenkgutscheine",
   * one variant per sofa-price tier): by product id, by the `_part` the
   * configurator stamps on its line, or by the `_cover_voucher` property the
   * PDP modal writes (kc-cover-voucher.js). It stands in for the cover.
   */
  function isVoucherLine(item, cfg) {
    if (cfg.voucherIds && cfg.voucherIds.indexOf(productId(item)) !== -1) return true;
    if (part(item) === "voucher") return true;
    return !!(item.properties && item.properties._cover_voucher);
  }

  function isCoverLine(item, cfg) {
    if (isVoucherLine(item, cfg)) return true;
    var type = String(item.product_type || "").toLowerCase();
    if (type === cfg.coverType) return true;
    if (hasCoverPrefix(item, cfg)) return true;
    return part(item) === "cover";
  }

  function isSofaLine(item, cfg) {
    var type = String(item.product_type || "").toLowerCase();
    if (cfg.sofaTypes.indexOf(type) !== -1) return true;
    if (part(item) === "parent") return true;

    // A "Stoffbezug - Nina Configuration" cover must never read as a sofa.
    if (hasCoverPrefix(item, cfg)) return false;

    var title = productTitle(item);
    var start = title.length - cfg.sofaSuffix.length;
    return start >= 0 && title.indexOf(cfg.sofaSuffix, start) === start;
  }

  /**
   * Is this line a paid PIECE of a 3D configuration (Sitz, Armlehne,
   * Eckelement...)? They are what actually costs money there — the parent
   * "X Configuration" line is 0.00 by design — so they are what counts
   * towards the qualifying threshold.
   *
   * Structural detection because /cart.js exposes no tags: configurator
   * type + the `_set_id` every configurator line carries, minus everything
   * that is NOT sofa structure (covers, cushion sets, the Schutzset, and
   * the 0-valued parent itself, excluded by price anyway).
   */
  function isConfigPieceLine(item, cfg) {
    // startsWith y no ===: /cart.js sirve el product_type TRADUCIDO al
    // locale del comprador, y en italiano 'configurator' es 'configuratore'
    // (verificado en la tienda CH). Un === dejaba el total calificante a 0
    // para cualquier comprador en italiano.
    if (String(item.product_type || "").toLowerCase().indexOf("configurat") !== 0) return false;
    if (!item.properties || !item.properties._set_id) return false;
    if (isCoverLine(item, cfg)) return false;
    if (isAnySetLine(item, cfg)) return false;
    if (isSchutzsetLine(item, cfg)) return false;
    // Los Kissen sueltos del configurador (harvey-kissen-p1 ... -p8, con el
    // handle tambien traducido: harvey-cushion-p1-24x75cm, cuscino-harvey-p1-…)
    // NO estan en las colecciones trigger de los BXGY, asi que no pueden
    // sumar aqui: contarlos enseniaria el regalo en un carrito al que el
    // descuento le va a decir que no. Los modulos reales no llevan -p<n>.
    if (/(^|-)p\d+(-|$)/.test(String(item.handle || "").toLowerCase())) return false;
    return true;
  }

  /**
   * CENTIMOS que suman para el umbral: sofas del storefront + piezas de
   * configuracion. El cover NO suma — gana el Schutzset, no el umbral.
   * Un sofa de modelo "solo" (Nora, bf[24]) tampoco: gana los regalos por
   * si mismo, sin umbral, y no completa el umbral de otro sofa (ver
   * giftStatus). Sin soloModels (markup viejo) el total es el de siempre.
   */
  function qualifyingTotal(cartItems, cfg) {
    var items = cartItems || [];
    return items.reduce(function (sum, item) {
      if (isSofaLine(item, cfg) && soloModelOf(item, cfg)) return sum;
      if (isSofaLine(item, cfg) || isConfigPieceLine(item, cfg)) {
        return sum + (Number(item.final_line_price) || 0);
      }
      return sum;
    }, 0);
  }

  /**
   * The discount codes ENTERED on a /cart.js snapshot, lowercase: the
   * `discount_codes` list where the API gives it (applicable or not), else
   * the code-type applications on the cart and on its lines.
   *
   * Entered, not applicable: the moment the gifts land, the Function zeroes
   * them, and a code that was not set to combine with product discounts is
   * then marked `applicable: false` by Shopify. Counting only applicable
   * codes read that as "not earned", removed the gifts, which brought the
   * code back, which added them again: a loop (20 Oct 2026). The Function
   * keys on the entered codes too (`enteredDiscountCodes`), so this agrees
   * with it. The cover's own percentage needs the code to be combinable
   * with product discounts in the admin; that is the merchant's setting,
   * not something either side can repair.
   */
  function appliedCodes(cart) {
    var codes = [];
    function push(code) {
      if (!code) return;
      code = String(code).toLowerCase();
      if (codes.indexOf(code) === -1) codes.push(code);
    }
    if (!cart) return codes;
    (cart.discount_codes || []).forEach(function (d) {
      if (d) push(d.code);
    });
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a && a.type === "discount_code") push(a.title);
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        var a = alloc && alloc.discount_application;
        if (a && a.type === "discount_code") push(a.title);
      });
    });
    return codes;
  }

  /** Is one of the campaign's gift codes among the applied ones? */
  function giftCodeApplied(codes, cfg) {
    if (!codes || !codes.length || !cfg.giftCodes || !cfg.giftCodes.length) return false;
    return codes.some(function (code) {
      return cfg.giftCodes.indexOf(String(code).toLowerCase()) !== -1;
    });
  }

  /**
   * Per-gift status over /cart.js items — derived, never stored, so it can't
   * go stale. `covered` counts the product being present at all, gifted OR
   * bought, from either surface: that is the owner's definition of "the
   * customer already has it".
   *
   * v2 (7 Sep 2026): ONE condition for both gifts — sofa AND cover, over the
   * threshold. A sofa alone earns nothing now (v1 gave it Set 1), and Set 2
   * is no longer a gift at all: `setB.eligible` is hard false, so the Set 2
   * lines v1 handed out read as orphans and leave the cart.
   *
   * Black Weeks 2026 (Nora, Oct 2026): a solo-model sofa (bf[24]) earns both
   * gifts on its own, and a model with its own set (bf[23]) gets that set
   * instead of Set 1 — `set.handle` / `set.productId` say which.
   */
  function giftStatus(cartItems, cfg, codes) {
    var items = cartItems || [];
    // Black Weeks 2026, Nora (bf[24]): a sofa of a SOLO model earns the two
    // gifts by itself (it has no Wechselbezug), with no cover and no minimum
    // — but it must be PAID (> 0), like every sofa here. It does not count as
    // a "normal" sofa: it neither needs a cover nor tops up another sofa's
    // minimum. `normalSofa` / `soloSofa` are the FIRST line of each kind;
    // they decide which cushion set is the gift (see `giftSofa` below).
    var normalSofa = null;
    var soloSofa = null;
    items.forEach(function (i) {
      if (!isSofaLine(i, cfg)) return;
      if (soloModelOf(i, cfg)) {
        if (!soloSofa && (Number(i.final_line_price) || 0) > 0) soloSofa = i;
      } else if (!normalSofa) {
        normalSofa = i;
      }
    });
    var hasSofa = !!normalSofa;
    // the COMPLETE cover, not any cover line (27/10, after CH #13633)
    var hasCover = hasCompleteCover(items, cfg);
    // ANY set, not only Set 1 (Black Weeks 2026): another set kept instead
    // of the gift means the gift is not pending, see isAnySetLine
    var hasSet = items.some(function (i) { return isAnySetLine(i, cfg); });
    var hasSchutzset = items.some(function (i) { return isCareLine(i, cfg); });

    // El sofa tiene que estar COMPRADO, no solo presente. `> 0` no es un
    // umbral comercial (ese es minAmount, 0 en DE / 250000 en CH): es lo que
    // separa "hay un sofa en el carrito" de "el cliente ha pagado un sofa".
    //
    // La linea padre de una configuracion ("Nina Configuration") vale 0,00 EUR
    // POR DISENO — lo que cuesta son las piezas — asi que un carrito cuyo
    // unico sofa es esa linea no ha comprado ningun sofa. Pedido DE-#19529:
    // un Stoffbezug de recambio (579 EUR) mas esa linea padre de 0,00 EUR
    // leyeron sofa + cover y se llevaron Kissen Set 1 + Komplettes Schutzset
    // gratis. La oferta es "sofa + su segundo cover"; un cover suelto, con o
    // sin la linea padre al lado, no la gana.
    //
    // Los sofas del storefront y las piezas de configurador reales siempre
    // suman > 0, asi que ningun carrito legitimo cambia. El cover nunca suma
    // aqui — gana los regalos, no el umbral — asi que su precio no puede
    // hacer pasar por sofa a un carrito que solo tiene cover.
    var qualifyingAmount = qualifyingTotal(items, cfg);
    var qualifies = qualifyingAmount > 0 && qualifyingAmount >= (cfg.minAmount || 0);
    // Black Weeks 2026, the post-purchase cover (the codes in bf[21]): a
    // customer who bought the sofa in an earlier order enters the code next
    // to the cover, and the code stands in for the sofa. The Function zeroes
    // the gifts on the same rule (`giftCodes`). MIRROR OF:
    // snippets/kc-bf-cart-gift.liquid `bf_code_applied`.
    var codeEarned = (hasCover || hasAnyWholeCover(items, cfg)) && giftCodeApplied(codes, cfg);
    var normalEarned = hasSofa && hasCover && qualifies;
    var soloEarned = !!soloSofa;
    var earned = normalEarned || codeEarned || soloEarned;

    // WHICH cushion set is the gift: the model gift (bf[23]) of the sofa that
    // earned it, else Kissen Set 1. Mixed cart (a Nora AND another sofa):
    // when the other sofa + cover qualifies by the normal rule, the normal
    // gifts win (Set 1 + Schutzset); otherwise the Nora earns them on its own
    // and the set is Kissen Set 8. One set either way — never both — and the
    // checkout's one-sofa-per-order rule (the Function gifts nothing with two
    // sofas) makes this a display question more than a money one. A gift
    // code without a qualifying sofa follows the Nora if there is one, else
    // Set 1. MIRROR OF: `bf_gift_haystack` in snippets/kc-bf-cart-gift.liquid.
    var giftSofa = normalEarned ? normalSofa : soloSofa || normalSofa;
    var modelGift = giftSofa ? modelGiftOf(giftSofa, cfg) : null;

    return {
      qualifies: qualifies,
      earned: earned,
      // "sofa" (sofa + cover over the minimum), "code" (gift code + cover),
      // "solo" (a solo-model sofa alone), or "" when nothing is earned
      earnedBy: normalEarned ? "sofa" : codeEarned ? "code" : soloEarned ? "solo" : "",
      set: {
        eligible: earned,
        covered: hasSet,
        pending: earned && !hasSet,
        // the set to add, and the `_bf_gift` it carries: Kissen Set 8 for a
        // Nora, Set 1 (bf[1], first of bf[10]) for everything else
        handle: modelGift ? modelGift.handle : cfg.giftA,
        productId: modelGift ? modelGift.productId : cfg.setIds[0] || "",
        model: modelGift ? modelGift.model : "",
      },
      // Retired gift: never earned, so kc-bf-cart-gift.js removes any
      // `_bf_gift` Set 2 line it still finds.
      setB: {
        eligible: false,
      },
      schutzset: {
        eligible: earned,
        covered: hasSchutzset,
        pending: earned && !hasSchutzset,
      },
    };
  }

  /**
   * Is this `_bf_gift` set line the set THIS cart earns (status.set)? A
   * `_bf_gift` Kissen Set 8 is the gift of a Nora cart, and only of one: in a
   * cart whose gift is Set 1 it is a stale gift, and so is a `_bf_gift`
   * Set 1 in a Nora cart. Lines are told apart by the `_bf_gift` value (the
   * gift handle, never translated), not by product: the configurator's gift
   * set is another product tagged with Set 1's handle. Answers true on a
   * status without `handle` (a caller on the old rules): old behaviour.
   */
  function matchesSetGift(item, status, cfg) {
    var set = status && status.set;
    if (!set || !set.handle) return true;
    var tagged = modelGiftByTag(item, cfg);
    if (set.model) return !!tagged && tagged.handle === set.handle;
    return !tagged;
  }

  /** Dev-only logging: ?bfDebug=1 once, then localStorage keeps it on. */
  var debugOn = false;
  try {
    if (window.location.search.indexOf("bfDebug=1") !== -1) {
      window.localStorage.setItem("bfDebug", "1");
    }
    debugOn = window.localStorage.getItem("bfDebug") === "1";
  } catch (error) {
    /* Safari private mode: no storage, no debug. */
  }

  function debug() {
    if (!debugOn) return;
    var args = ["[bf-rules]"].concat(Array.prototype.slice.call(arguments));
    console.debug.apply(console, args);
  }

  window.KcBfRules = {
    readConfig: readConfig,
    isSetLine: isSetLine,
    isAnySetLine: isAnySetLine,
    isSetBLine: isSetBLine,
    isVoucherLine: isVoucherLine,
    isCoverLine: isCoverLine,
    hasCompleteCover: hasCompleteCover,
    appliedCodes: appliedCodes,
    isSofaLine: isSofaLine,
    isSchutzsetLine: isSchutzsetLine,
    isCareLine: isCareLine,
    isConfigPieceLine: isConfigPieceLine,
    qualifyingTotal: qualifyingTotal,
    giftStatus: giftStatus,
    soloModelOf: soloModelOf,
    modelGiftOf: modelGiftOf,
    modelGiftByProduct: modelGiftByProduct,
    modelGiftByTag: modelGiftByTag,
    matchesSetGift: matchesSetGift,
    debug: debug,
  };
})();
